import {
  createFrontendPlugin,
  FrontendPlugin,
  PageBlueprint,
  ApiBlueprint,
} from '@backstage/frontend-plugin-api';
import { discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import {
  FormFieldBlueprint,
  createFormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import { vaultApiRef } from './api/VaultApi';
import { VaultClient } from './api/VaultClient';

const vaultApi = ApiBlueprint.make({
  name: 'vault-requests',
  params: defineParams =>
    defineParams({
      api: vaultApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new VaultClient({ discoveryApi, fetchApi }),
    }),
});

// NOTE: PageBlueprint/EntityCardBlueprint/EntityContentBlueprint loaders must
// resolve to a rendered React *element* (`<Component />`), not the component
// function. Returning the function (previously `m.X as any`) renders to nothing
// — the cause of the blank Vault Requests page (#242).
const requestsPage = PageBlueprint.make({
  name: 'vault-requests',
  params: {
    path: '/vault-requests',
    title: 'Vault Requests',
    loader: () => import('./pages/RequestsPage').then(m => <m.RequestsPage />),
  },
});

const settingsPage = PageBlueprint.make({
  name: 'vault-settings',
  params: {
    path: '/vault-settings',
    title: 'Vault Settings',
    loader: () =>
      import('./pages/VaultSettingsPage').then(m => <m.VaultSettingsPage />),
  },
});

const overviewCard = EntityCardBlueprint.make({
  name: 'vault-request-overview',
  params: {
    filter: 'kind:component,spec.type=vault-request',
    loader: () =>
      import('./components/EntityVaultRequestOverviewCard').then(m => (
        <m.EntityVaultRequestOverviewCard />
      )),
  },
});

const trailContent = EntityContentBlueprint.make({
  name: 'vault-request-trail',
  params: {
    path: '/trail',
    title: 'State Trail',
    filter: 'kind:component,spec.type=vault-request',
    loader: () =>
      import('./components/EntityVaultRequestTrailCard').then(m => (
        <m.EntityVaultRequestTrailCard />
      )),
  },
});

const adminContent = EntityContentBlueprint.make({
  name: 'vault-request-admin',
  params: {
    path: '/admin',
    title: 'Admin',
    filter: 'kind:component,spec.type=vault-request',
    loader: () =>
      import('./components/EntityVaultRequestAdminCard').then(m => (
        <m.EntityVaultRequestAdminCard />
      )),
  },
});

const vaultOnboardingFormField = FormFieldBlueprint.make({
  name: 'VaultOnboardingFormField',
  params: {
    field: () =>
      import('./scaffolder').then(m =>
        createFormField({
          name: 'VaultOnboardingFormField',
          component: m.VaultOnboardingFormField,
        }),
      ),
  },
});

export const vaultRequestsPlugin: FrontendPlugin = createFrontendPlugin({
  pluginId: 'vault-requests',
  extensions: [
    vaultApi,
    requestsPage,
    settingsPage,
    overviewCard,
    trailContent,
    adminContent,
    vaultOnboardingFormField,
  ],
});
